import React, { useState } from "react";
import {
  ANALYSIS,
  APPEAL_ICON_BOX,
  CHEVRON_DOWN,
  DATE,
  DISMISSED_ICON_BOX,
  EDIT,
  GREEN_ARROW_UP,
  PENDING_ICON_BOX,
  PROFILE,
  PROFILE_ADD,
  RED_ARROW_UP,
  SEARCH_ICON_BOX,
  TRASH,
  USERS_GREEN,
} from "../../assets";
import { useQuery } from "react-query";
import {
  deleteUser,
  getAdminDashboardSummary,
  getAdminSearchStats,
  getAdminUsers,
} from "../../Services/admin.service";
import ButtonV2 from "../../shared/components/buttonV2";
import {
  BarChart,
  Bar,
  XAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import DeleteModal from "../../shared/components/deleteModal";
import { toast } from "react-hot-toast";
import AddUserModal from "../../shared/components/addusersModal";
import LinkHead from "../../shared/components/LinkHead";

function AdminDashboard() {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState("This Month");
  const { data: dashboardSummary } = useQuery(
    "summary",
    getAdminDashboardSummary,
    {
      refetchOnWindowFocus: false,
    }
  );
  const { data: dashboardStats } = useQuery("stats", getAdminSearchStats, {
    refetchOnWindowFocus: false,
  });
  const { data: users, refetch: refetchUsers } = useQuery(
    ["users", 0],
    () => getAdminUsers(0),
    {
      refetchOnWindowFocus: false,
    }
  );

  const chartData = dashboardStats?.data?.monthly?.map(
    (item: any, index: number) => ({
      name: dashboardStats?.data?.labels[index],
      value: Number(item),
    })
  );
  const [userModal, setUserModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const response = await deleteUser(selectedUser?.id);
      if (response) {
        toast.success(response?.message);
        setDeleteModal(false);
        refetchUsers();
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    } finally {
      setDeleting(false);
    }
  };

  const handleSelect = (option: string) => {
    setSelected(option);
    setOpen(false);
  };

  const getStats = () => {
    switch (selected) {
      case "This Month":
        return `${dashboardStats?.data?.thisMonth || 0}%`;

        break;

      case "Last Month":
        return `${dashboardStats?.data?.prev_month_percentage_change || 0}%`;
        break;
      case "Last Year":
        return dashboardStats?.data?.prev_year_percentage_change !== null
          ? `${dashboardStats?.data?.prev_year_percentage_change || 0}%`
          : "N/A";
      default:
        return "-";
    }
  };

  const value = getStats().replace("%", "");

  return (
    <div className="p-3 sm:p-5">
      <LinkHead title="Admin - dashboard" content="" />
      <div className="flex items-start flex-col">
        <span className="text-[#2A2E34] font-semibold text-xl sm:text-2xl">
          Dashboard
        </span>
        <span className="text-primary-50 font-medium text-sm">
          Dashboard Overview
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5 mt-5">
        <div className="w-full rounded-xl border border-borderColor p-3 flex items-start gap-x-2">
          <img
            src={SEARCH_ICON_BOX}
            className="w-8 h-8 sm:w-[40px] sm:h-[40px] flex-shrink-0"
            alt=""
          />
          <div className="flex flex-col min-w-0">
            <span className="text-xs font-medium text-primary-50">
              Total Searches
            </span>
            <span className="text-lg sm:text-xl font-bold text-customDark truncate">
              {dashboardSummary?.total_searches}
            </span>
          </div>
        </div>
        <div className="w-full rounded-xl border border-borderColor p-3 flex items-start gap-x-2">
          <img
            src={PENDING_ICON_BOX}
            className="w-8 h-8 sm:w-[40px] sm:h-[40px] flex-shrink-0"
            alt=""
          />
          <div className="flex flex-col min-w-0">
            <span className="text-xs font-medium text-primary-50">
              Total Pending
            </span>
            <span className="text-lg sm:text-xl font-bold text-customDark truncate">
              {dashboardSummary?.total_pending}
            </span>
          </div>
        </div>
        <div className="w-full rounded-xl border border-borderColor p-3 flex items-start gap-x-2">
          <img
            src={APPEAL_ICON_BOX}
            className="w-8 h-8 sm:w-[40px] sm:h-[40px] flex-shrink-0"
            alt=""
          />
          <div className="flex flex-col min-w-0">
            <span className="text-xs font-medium text-primary-50">
              Total Appeal
            </span>
            <span className="text-lg sm:text-xl font-bold text-customDark truncate">
              {dashboardSummary?.total_on_appeal}
            </span>
          </div>
        </div>
        <div className="w-full rounded-xl border border-borderColor p-3 flex items-start gap-x-2">
          <img
            src={DISMISSED_ICON_BOX}
            className="w-8 h-8 sm:w-[40px] sm:h-[40px] flex-shrink-0"
            alt=""
          />
          <div className="flex flex-col min-w-0">
            <span className="text-xs font-medium text-primary-50">
              Total Dismissed
            </span>
            <span className="text-lg sm:text-xl font-bold text-customDark truncate">
              {dashboardSummary?.total_concluded}
            </span>
          </div>
        </div>
      </div>

      <div className="mt-5 flex flex-col lg:flex-row items-start gap-3">
        <div className="w-full lg:w-[600px] border h-full border-borderColor rounded-xl">
          <div className="w-full flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 bg-lightGold rounded-t-xl gap-2">
            <div className="flex items-center gap-x-1">
              <img src={USERS_GREEN} className="w-[20px] h-[20px]" alt="" />
              <span className="text-sm sm:text-[16px] font-medium">Users</span>
            </div>
            <ButtonV2
              title={"Add New User"}
              image={PROFILE_ADD}
              btnStyle={
                "flex items-center justify-center gap-x-1 bg-globalGold px-3 py-2 rounded-lg flex-row-reverse"
              }
              textStyle={"text-[#FAFAFA] font-medium text-xs"}
              handleClick={() => setUserModal(true)}
            />
          </div>
          <AddUserModal
            open={userModal}
            close={() => setUserModal(false)}
            refetch={refetchUsers}
          />

          <div className="flex flex-col w-full max-h-[450px] overflow-y-scroll">
            {users?.data?.map((item: { id: number; name: string }) => (
              <div
                key={item.id}
                className="flex items-center justify-between w-full p-3"
              >
                <div className="flex items-center gap-x-2">
                  <img src={PROFILE} className="w-[30px] h-[30px]" alt="" />
                  <span className="text-sm font-medium text-[#4D4D4D]">
                    {item.name.replace(/_/g, " ")}
                  </span>
                </div>
                <div className="flex items-center gap-x-1">
                  <button
                    className="hover:opacity-80"
                    onClick={() => {
                      setSelectedUser(item);
                      setDeleteModal(true);
                    }}
                  >
                    {" "}
                    <img src={TRASH} className="w-[32px] h-[32px]" alt="" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="w-full border border-borderColor rounded-xl">
          <div className="w-full flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 bg-lightGold rounded-t-xl gap-2">
            <div className="flex items-center gap-x-1">
              <img src={ANALYSIS} className="w-[20px] h-[20px]" alt="" />
              <span className="text-sm sm:text-[16px] font-medium">
                Data Reports
              </span>
            </div>
            <div className="flex items-center gap-x-2">
              <div className="relative">
                <div
                  onClick={() => setOpen(!open)}
                  className="w-fit p-2 flex space-x-2 items-center justify-between bg-white border border-[#E5E7EF] rounded-xl cursor-pointer"
                >
                  <div className="flex items-center gap-x-1 w-full">
                    <img src={DATE} className="w-[14px] h-[14px]" alt="" />
                    <span className="text-xs font-medium text-customDark">
                      {selected}
                    </span>
                  </div>
                  <img
                    src={CHEVRON_DOWN}
                    className="w-[14px] h-[14px]"
                    alt=""
                  />
                </div>

                {open && (
                  <div className="absolute right-0 mt-1 w-[150px] rounded-md shadow-lg bg-white border border-gray-200 z-10">
                    {["This Month", "Last Month", "Last Year"].map((option) => (
                      <div
                        key={option}
                        onClick={() => handleSelect(option)}
                        className="px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                      >
                        {option}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-col items-start gap-y-5 px-3 sm:px-5 py-7">
            <div className="flex flex-col items-start">
              <span className="text-2xl sm:text-[32px] font-semibold text-[#080808] break-words">
                {dashboardStats?.data?.total_for_the_year} Searches
              </span>
              <div className="flex items-center gap-x-2">
                <div
                  className={`w-fit px-2 py-1 rounded-3xl flex items-center justify-center border 
      ${
        value === "N/A"
          ? "bg-gray-100 border-gray-300"
          : Number(value) >= 0
          ? "bg-lightGold border-globalGold"
          : "bg-red-100 border-red-300"
      }`}
                >
                  <img
                    src={
                      value === "N/A"
                        ? ""
                        : Number(value) >= 0
                        ? GREEN_ARROW_UP
                        : RED_ARROW_UP
                    }
                    className="w-[14px] h-[14px]"
                    alt=""
                  />

                  <span
                    className={`font-medium text-sm ml-1 ${
                      value === "N/A"
                        ? "text-gray-500"
                        : Number(value) >= 0
                        ? "text-globalGold"
                        : "text-red-600"
                    }`}
                  >
                    {getStats()}
                  </span>
                </div>

                {/* Label */}
                <span className="text-[#878787] font-medium text-[16px] ">
                  vs {selected.toLowerCase()}
                </span>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <XAxis dataKey="name" />
                <Tooltip />
                <Bar dataKey="value" radius={[10, 10, 0, 0]}>
                  {chartData?.map((entry: any, index: number) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.value > 0 ? "#FFAC06" : "#e5e7eb"}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
      <DeleteModal
        open={deleteModal}
        close={() => setDeleteModal(false)}
        deleteTitle={"Users"}
        warningMessage={`the user “${selectedUser?.name}”`}
        lastmessage={"users"}
        handleDelete={handleDelete}
        loading={deleting}
      />
    </div>
  );
}

export default AdminDashboard;
